<script lang="ts">
    import { Accordion } from "@/Common";
    import { defaultTrainingPlanSettings, type TrainingPlanSettings } from "@/Common/constants";
    import { PeriodizationSlider, PolarizationSlider, ProgramDifficultySlider, RecoverySlider } from "./SetupFormInputs";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";
    import { bootstrapConfirmation } from "@/Bootstrap";

    export let settings: TrainingPlanSettings
    export let disabled = false
    export let alwaysOpen = false
    export let showDefaultsButton = true
    // Plain layout to sit alongside the Calendar Settings fields: no card border, label above each control.
    export let compact = false
    // Each label + control pair is one grid item in compact mode; `contents` keeps the original two-column grid otherwise.
    // In compact mode the short fields (Recovery, Max Weekly Hours) share a row ahead of the taller Difficulty field.
    const order = ['order-1', 'order-2', 'order-3', 'order-4', 'order-5', 'order-6']
    $: fieldClass = (n: number) => compact ? `min-w-0 ${order[n]}` : 'contents'
    export let onInput = () => {}
    export let calculatedDifficulty: number = undefined
    let className = ""
    export {className as class}

    function resetToDefaults() {
        settings = {
            ...settings,
            periodization: defaultTrainingPlanSettings.periodization,
            polarization: defaultTrainingPlanSettings.polarization,
            recovery: defaultTrainingPlanSettings.recovery,
        }
    }

</script>

<div class="{className} {compact ? 'compact' : ''}">
    <Accordion {alwaysOpen} class={compact ? '' : 'py-2 px-4 border border-solid border-black/10 rounded'}>
        <svelte:fragment slot="trigger">
            {#if !alwaysOpen}
                <div class="text-slate-600 text-left">General Settings</div>
            {:else}
                Program Settings
            {/if}
        </svelte:fragment>

        <div slot="content" class="grid {compact ? 'grid-cols-1 lg:grid-cols-2 gap-y-4 gap-x-10 items-start' : 'grid-cols-[1fr,3fr] gap-y-9 gap-x-3 md:gap-x-6'} auto-rows-max {compact ? '' : 'items-baseline'} text-left {compact ? 'text-sm' : 'text-base'} {alwaysOpen || compact ? '' : 'md:text-right'}">

            <div class={fieldClass(0)}>
            <span class={compact ? '' : 'text-slate-600 text-sm'}>Recovery Demands
                <HelpTooltip message="Controls how much recovery your program builds in between hard efforts. Move it toward &ldquo;More&rdquo; if you need more rest between sessions, or toward &ldquo;Less&rdquo; if you recover quickly and want a tighter training schedule. <a href='https://baronbiosys.com/glossary/recovery-demands/' target='_help'>Learn more</a>" iconClass="w-5 h-5 text-gray-500" />
            </span>
            <RecoverySlider {disabled}
                bind:recovery={settings.recovery}
                onInput={onInput}
            />
            </div>

            <div class={fieldClass(2)}>
            <span class={compact ? '' : 'text-slate-600 text-sm'}>Preferred Workout Difficulty
                <HelpTooltip message="Sets how difficult the workouts and activities recommended to you should be. Recommendations are scored against this target &mdash; the closer a workout's difficulty is to this value, the more strongly it's recommended, with workouts harder than your target penalized more than ones that are easier. Turn on &ldquo;Based on Training Load&rdquo; to have this set automatically from your current fitness instead of a fixed number. A higher difficulty also puts more training into each hour you're available, so a Goal plan can improve your power more (its Estimated Frontier rises)." iconClass="w-5 h-5 text-gray-500" />
            </span>
            <ProgramDifficultySlider
                bind:difficulty={settings.difficulty}
                bind:difficultyFromTrainingLoad={settings.difficultyFromTrainingLoad}
                {calculatedDifficulty}
                onInput={onInput}
            />
            </div>

            <div class={fieldClass(1)}><slot /></div> <!-- For additional settings -->

            <div class="col-span-full text-left {compact ? 'mt-2 font-semibold order-4' : 'text-sm font-semibold text-slate-700'}">Advanced Program Settings</div>

            <div class={fieldClass(4)}>
            <span class={compact ? '' : 'text-slate-600 text-sm'}>Periodization Level
                <HelpTooltip message="Controls how much your program cycles between harder training blocks and easier recovery blocks over time, instead of applying a steady load every week. Raise it for a stronger build-and-recover wave in your schedule; leave it at 0% for a flat, unperiodized progression. <a href='https://baronbiosys.com/glossary/periodization-level/' target='_help'>Learn more</a>" iconClass="w-5 h-5 text-gray-500" />
            </span>
            <PeriodizationSlider {disabled}
                bind:periodization={settings.periodization}
                onInput={onInput}
            />
            </div>

            <div class={fieldClass(5)}>
            <span class={compact ? '' : 'text-slate-600 text-sm'}>Polarization Level
                <HelpTooltip message="Controls the mix of training intensities in your recommended workouts, shown as a ratio of easier to harder training. A higher ratio biases your program toward more low-intensity endurance work and less high-intensity work; 1:1 is a balanced, mixed approach. <a href='https://baronbiosys.com/glossary/polarization-level/' target='_help'>Learn more</a>" iconClass="w-5 h-5 text-gray-500" />
            </span>
            <PolarizationSlider {disabled}
                bind:polarization={settings.polarization}
                onInput={onInput}
            />
            </div>

            {#if showDefaultsButton}
                <button 
                    use:bootstrapConfirmation={{
                        onclick: resetToDefaults,
                        placement:"top",
                        okMessage: "Reset",
                        cancelMessage: "Cancel",
                        title: "Restore General Settings to Xert Defaults ?",
                    }} 
                    class="btn-xfai-sm border-0 text-xerteal-500 hover:underline bg-transparent col-span-full justify-self-end" type="button"
                >
                    Defaults
                </button>
            {/if}
        </div>
    </Accordion>
</div>

<style lang="scss">
    // Heading of the compact layout reads as a section title within Calendar Settings.
    .compact :global(h4) {
        font-size: 1.1em;
        font-weight: 600;
        margin: 0;
    }
</style>
